// Cola local de impresión del POS de evento. Es un singleton de módulo, fuera
// del ciclo de vida de cualquier diálogo: serializa los trabajos de todas las
// ventas para que dos cobros seguidos no superpongan diálogos ni alteren el
// orden factura → comanda, y sigue corriendo aunque el cajero cierre el
// diálogo e inicie la venta siguiente.
//
// Cada trabajo monta un iframe temporal del mismo origen, espera el contenido
// (imágenes y fuentes) y llama a contentWindow.print(). Con la política
// SilentPrintingEnabled el navegador imprime en la predeterminada sin diálogo;
// sin ella muestra la vista previa. En los dos casos lo único que se puede
// afirmar es que el trabajo se SOLICITÓ: afterprint también ocurre al cancelar
// la vista previa y window.print() no devuelve resultado físico.

export const TIEMPO_ESPERA_CONTENIDO_MS = 8000;
export const TIEMPO_ESPERA_AFTERPRINT_MS = 30000;

// Promesa siempre resuelta: un trabajo fallido nunca corta la cadena.
let cadena = Promise.resolve();
let trabajosEnCola = 0;

export const trabajosPendientes = () => trabajosEnCola;

// Imágenes (el QR es un data URI) y fuentes del documento ya escrito. Con un
// tope: si algo nunca carga, se imprime lo que hay antes que bloquear la cola.
const esperarContenido = (doc) =>
  new Promise((resolve) => {
    let listo = false;
    const terminar = () => {
      if (listo) return;
      listo = true;
      clearTimeout(timeout);
      resolve();
    };
    const timeout = setTimeout(terminar, TIEMPO_ESPERA_CONTENIDO_MS);
    const imagenes = Array.from(doc?.images || []);
    Promise.all(
      imagenes.map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((done) => {
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
              })
      )
    )
      .then(() => (doc?.fonts?.ready ? doc.fonts.ready : null))
      .catch(() => null)
      .then(terminar);
  });

const crearIframe = () => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("title", "Impresión del punto de venta");
  // Fuera de la vista pero con layout real: un iframe display:none imprime en
  // blanco en algunos navegadores.
  Object.assign(iframe.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: "320px",
    height: "600px",
    border: "0",
    opacity: "0",
    pointerEvents: "none",
  });
  return iframe;
};

// Se escribe el documento en el iframe about:blank del mismo origen, como ya
// hace printFactura con su ventana emergente. El <title> del documento es el
// nombre del trabajo en la cola del sistema.
const escribirDocumento = (iframe, html) => {
  const doc = iframe.contentDocument;
  if (!doc) throw new Error("No se pudo montar el documento de impresión");
  doc.open();
  doc.write(html);
  doc.close();
  return doc;
};

const ejecutarTrabajo = ({ html }) =>
  new Promise((resolve, reject) => {
    if (typeof document === "undefined" || !document.body) {
      reject(new Error("No hay un documento donde montar la impresión"));
      return;
    }

    const iframe = crearIframe();
    let finalizado = false;
    let timeout = null;

    const limpiar = () => {
      clearTimeout(timeout);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };
    const finalizar = () => {
      if (finalizado) return;
      finalizado = true;
      limpiar();
      resolve();
    };
    const fallar = (error) => {
      if (finalizado) return;
      finalizado = true;
      limpiar();
      reject(error);
    };

    try {
      document.body.appendChild(iframe);
      const doc = escribirDocumento(iframe, html);
      esperarContenido(doc)
        .then(() => {
          const win = iframe.contentWindow;
          if (!win || typeof win.print !== "function") {
            throw new Error("El navegador no expone la impresión del documento");
          }
          win.addEventListener("afterprint", finalizar, { once: true });
          // Si el navegador no emite afterprint, la cola no se bloquea.
          timeout = setTimeout(finalizar, TIEMPO_ESPERA_AFTERPRINT_MS);
          win.focus();
          win.print();
        })
        .catch(fallar);
    } catch (error) {
      fallar(error);
    }
  });

// Encola un documento HTML completo. Resuelve cuando el navegador terminó el
// ciclo de impresión (afterprint o timeout); rechaza sólo por un error local
// al montar o invocar la impresión.
export const encolarImpresion = ({ html }) => {
  trabajosEnCola += 1;
  const trabajo = cadena
    .then(() => ejecutarTrabajo({ html }))
    .finally(() => {
      trabajosEnCola -= 1;
    });
  cadena = trabajo.catch(() => {});
  return trabajo;
};
